import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, authApi, errorMessage } from "@/api/client";
import { Alert, Field, PasswordField } from "@/ui";

const schema = z.object({
  email: z.string().email("Введите email"),
  password: z.string().min(1, "Введите пароль"),
  remember: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const { register, handleSubmit, formState } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { remember: true, email: "", password: "" },
  });
  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  const onSubmit = async (f: Form) => {
    setError(null);
    setUnverified(null);
    try {
      await login(f.email, f.password, f.remember);
      nav(from, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === "email_not_verified") setUnverified(f.email);
      else setError(errorMessage(e));
    }
  };

  const resend = async () => {
    if (!unverified) return;
    await authApi.POST("/api/v1/auth/resend-verification", { body: { email: unverified } });
    setResent(true);
  };

  return (
    <form className="auth-card" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div>
        <h1>Войти</h1>
        <p className="auth-card__sub">
          Нет аккаунта? <Link to="/register">Создать</Link>
        </p>
      </div>
      {(loc.state as { message?: string } | null)?.message && (
        <Alert kind="success">{(loc.state as { message: string }).message}</Alert>
      )}
      {error && <Alert kind="error">{error}</Alert>}
      {unverified && (
        <Alert kind="warning">
          Email не подтверждён. Проверьте почту.{" "}
          {resent ? (
            "Письмо отправлено ещё раз."
          ) : (
            <button type="button" className="btn btn--ghost btn--sm" onClick={resend}>
              Отправить письмо снова
            </button>
          )}
        </Alert>
      )}
      <Field
        label="Email"
        type="email"
        placeholder="you@example.com"
        autoComplete="email"
        error={formState.errors.email?.message}
        {...register("email")}
      />
      <PasswordField
        label="Пароль"
        placeholder="••••••••"
        autoComplete="current-password"
        aside={<Link to="/forgot">Забыли пароль?</Link>}
        error={formState.errors.password?.message}
        {...register("password")}
      />
      <label className="checkbox">
        <input type="checkbox" {...register("remember")} />
        <span>Запомнить меня на этом устройстве</span>
      </label>
      <button className="btn btn--block" type="submit" disabled={formState.isSubmitting}>
        Войти
      </button>
    </form>
  );
}
